import { FormStore, formValuesToConsultationPayload } from '../stores/form-values-store';
import { AppointmentSlot, FormValues, ParsedFormValues, FunnelRule, RuleSet } from '../types';

import { serializeFormRaw as serializeForm } from '../../general/utils/consultation/serialize-form';
import { validate } from '../../general/utils/validation';
import { checkboxInput, InputController, radioInput, textInput } from '../../general/forms/form-input';
import { getFormattedDate, getFormattedTime } from '../../general/utils/date';
import { FormStepStore } from '../stores/form-step-store';
import { fillAppointment } from '../../general/utils/consultation/calendar';
import { ReadableAtom, atom, computed } from 'nanostores';

import { BookedAppointmentStore } from '../stores/booked-appointment-store';
import dayjs from 'dayjs';
import { startSpan as sentryStartSpan, captureException, captureMessage, metrics } from '@sentry/browser';
import { ConsultationResponse, AppointmentStatus, Language, AppointmentDetails, CalendarType } from '../../types/consultation';
import { CaptchaController, initCaptchaForForm } from '../../general/utils/captcha-v2';
import attachTrackingPayload from '../../general/utils/tracking-payload';
import { getPreviousAppointment, setPreviousAppointment } from '../../general/utils/consultation/appointment-service';
import { slideDown, slideUp } from '../../general/startup/slide';
import { initImage } from '../../general/startup/lazy-images';
import MicroModal from '../../addons/micro-modal';
import { createPhoneLeadEvent } from '../funnel-tracking';
import { TagType, addTrackingToElement } from '../../general/utils/ga4-tracking/automatic-link-tracking';
import { getAgeFromDoB } from '../../general/utils/date';
import { NotificationContainerHandler } from '../../general/forms/form-library';
import { createItiPhoneInput } from '../../addons/expatmodal';
import { Iso2 } from 'intl-tel-input/data';
import { getCalendarFromCategory } from '../stores/form-values-store';
import { postToLeadWithCaptcha } from '../../general/utils/post-to-lead-with-captcha';

import { getLeadDimensions, getPageDimensions } from '../../general/utils/ga4-tracking/custom-dimensions';
import { pushToDataLayer } from '../../general/utils/ga4-tracking/push-to-data-layer';

declare global {
  interface Window {
    initTooltip: (element: HTMLElement) => void;
  }
}

const en = window.en;
// Small helper so we can sprinkle spans without breaking older browsers.
const startSpan = <T>(name: string, op: string, attributes: Record<string, unknown>, cb: () => T) => (sentryStartSpan as any)?.({ name, op, attributes }, cb) ?? cb();

enum StepStatus {
  Idle = 'idle',
  Submitting = 'submitting',
  Submitted = 'submitted',
  Error = 'error',
  SubmitError = 'submit-error',
  Taken = 'taken'
}

type BranchingRules = {
  inputName: string;
  value: string | [number, number];
  nextSlideId: string;
}[];

const progressText: string = window.progressText;

// initializer for form steps
export default async function initStep(
  title: string,
  stepContent: DocumentFragment | HTMLElement,
  stores: {
    formStore: FormStore;
    progressStore: FormStepStore;
    calendarStore?: ReadableAtom<AppointmentSlot[] | Promise<AppointmentSlot[]>>;
    bookedAppointmentStore?: BookedAppointmentStore;
  },
  funnelRules: FunnelRule[]
) {
  const { formStore, progressStore, calendarStore, bookedAppointmentStore } = stores;
  const initialFormValues = formStore.values.get();

  const stepStatus = atom<StepStatus>(StepStatus.Idle);

  const alerts = {
    error: stepContent.querySelector<HTMLElement>('.alert'),
    taken: stepContent.querySelector<HTMLElement>('.alert.taken')
  };
  const alert = computed(stepStatus, $stepStatus => {
    if ($stepStatus === StepStatus.SubmitError || $stepStatus === StepStatus.Error || $stepStatus === StepStatus.Taken) {
      return $stepStatus;
    }
  });

  const dispatchSubmitEvent = (stepContent as HTMLElement).closest('.funnel-page-block')?.getAttribute('data-dispatch-submit-event') !== null;
  const isLastStep = !!(stepContent as HTMLElement).querySelector('button[data-action="submit"]');

  if (dispatchSubmitEvent) {
    document.dispatchEvent(
      new CustomEvent('funnelSubmitStep', {
        detail: {
          formValues: formStore.values.get(),
          isLastStep: isLastStep
        }
      })
    );
  }

  if (progressStore.isAlreadyBookedStep.get()) {
    const bookedAppointmentStoreState = bookedAppointmentStore?.store.get();

    if (!bookedAppointmentStoreState?.appointmentId) {
      const h2 = stepContent.querySelector<HTMLElement>('.content h2');
      if (h2) {
        h2.textContent = en ? 'You already booked a callback with us.' : 'Du hast bereits einen Rückruf bei uns gebucht.';
      }
      const consentStateDiv = stepContent.querySelector('.content > div[data-consent-state]');
      if (consentStateDiv) {
        consentStateDiv.innerHTML = en
          ? `We will call you as soon as possible! ${bookedAppointmentStoreState?.consent_confirmed ? 'We are looking forward to meet you!' : 'If you did not confirm your email address yet you we additionally sent you an email to do so for further communication.'}`
          : `Wir melden uns schnellstmöglich bei dir! ${bookedAppointmentStoreState?.consent_confirmed ? 'Wir freuen uns, dich kennenzulernen!' : 'Solltest du deine E-Mail-Adresse noch nicht bestätigt haben, haben wir dir zusätzlich eine E-Mail geschickt, damit du diese für die weitere Kommunikation bestätigen kannst.'}`;
      }
      stepContent.querySelector('.content > div[data-funnel-placeholder="consultation_date"]')?.remove();
    }
  }

  if (progressStore.isResultStep.get() && stepContent.querySelector<HTMLElement>('[data-funnel-slide]')) {
    const bookedAppointmentStoreState = bookedAppointmentStore?.store.get();

    const appointmentOrPromise = bookedAppointmentStoreState?.appointment;
    const appointmentInformation = appointmentOrPromise instanceof Promise ? await appointmentOrPromise : appointmentOrPromise;

    const alreadyBooked = false;
    const newConsultation = true;
    const isAppointment = !!appointmentInformation?.status;
    const isCallback = !isAppointment;
    const doiConfirmed = appointmentInformation?.status === 'confirmed' || bookedAppointmentStoreState?.consent_confirmed;
    const doiPending = !doiConfirmed;
    const doiRequiredPending = isAppointment && appointmentInformation?.doi_enabled && !doiConfirmed;
    const doiNotRequiredPending = !doiRequiredPending;
    const doiOptionalPending = !appointmentInformation?.doi_enabled && !doiConfirmed;

    // Apply visibility logic based on conditions
    const visibilityConditions = {
      'data-already-booked': alreadyBooked,
      'data-new-consultation': newConsultation,
      'data-is-appointment': isAppointment,
      'data-is-callback': isCallback,
      'data-doi-confirmed': doiConfirmed,
      'data-doi-pending': doiPending,
      'data-doi-required-pending': doiRequiredPending,
      'data-doi-not-required-pending': doiNotRequiredPending,
      'data-doi-optional-pending': doiOptionalPending
    };

    const elementsToToggle = stepContent.querySelectorAll(
      Object.keys(visibilityConditions)
        .map(key => `[${key}]`)
        .join(', ')
    );

    elementsToToggle.forEach(element => {
      let shouldShow = true;

      for (const [attr, value] of Object.entries(visibilityConditions)) {
        if (element.hasAttribute(attr) && !value) {
          shouldShow = false;
          break;
        }
      }

      element.classList.toggle('is-hidden', !shouldShow);
    });

    if (isAppointment) {
      const date = dayjs(appointmentInformation.start);
      stepContent.querySelector('#booked-date')!.innerHTML = getFormattedDate(date.toDate()).replace(' dem', '');
      stepContent.querySelector('#booked-time')!.innerHTML = getFormattedTime(date.toDate());
    }

    const emailDomains = ['gmail', 'googlemail', 'outlook', 'web', 'gmx'];
    const emailDomain = (bookedAppointmentStoreState?.email || '').split('@')[1];

    if (doiPending && emailDomain && emailDomains.some(domain => emailDomain.includes(domain))) {
      stepContent.querySelector('.email-buttons-container')?.classList.add('email-match');
      stepContent
        .querySelectorAll<HTMLAnchorElement>('.email-btns > a')
        .forEach(btn => btn.classList.toggle('is-hidden', !btn.dataset.emailDomain?.split(',').some(domain => emailDomain.includes(domain))));
    }

    //start SVG Animation on resultSlide
    if (doiRequiredPending) {
      const svgAnimationWatch = stepContent.querySelector<SVGAnimateElement>('.form-slide.result-slide .minutes svg .circle.cfill animate');
      if (svgAnimationWatch) {
        setTimeout(() => {
          svgAnimationWatch.beginElement();
        }, 500);
      }
    }
  }
  // @ts-ignore
  const notificationHandler = new NotificationContainerHandler(stepContent);

  alert.subscribe($alert => {
    if ($alert !== StepStatus.Taken && alerts.taken?.style.display === 'block') {
      slideUp(alerts.taken);
    }
    switch ($alert) {
      case StepStatus.Taken:
        notificationHandler.setAriaMessage(
          alerts.taken!.textContent!,
          Array.from(stepContent.querySelectorAll('select.dateSelect, select.timeSelect')) as (HTMLInputElement | HTMLSelectElement)[],
          document.querySelector<HTMLButtonElement>('.funnel-step button[data-action="next"]')
        );
        alerts.taken && slideDown(alerts.taken);
        break;
      case StepStatus.SubmitError:
        alerts.error && slideDown(alerts.error);
        notificationHandler.setAriaMessage(alerts.error!.textContent!, [], document.querySelector<HTMLButtonElement>('.funnel-step button[data-action="next"]'), true);

        pushToDataLayer({ event: 'error', error_type: 'lead_submission_failed', element_section: 'marketing-funnel-block' });
        progressStore.nextStep('error');
        break;
      case StepStatus.Error:
        notificationHandler.setAriaMessage(alerts.error!.textContent!, [], document.querySelector<HTMLButtonElement>('.funnel-step button[data-action="next"]'), true);
        alerts.error && slideDown(alerts.error);
        progressStore.nextStep('error');
        break;
      default:
        break;
    }
  });

  // create field/value object for combinations

  const radioInputs = Array.from(stepContent.querySelectorAll<HTMLInputElement>('input[type="radio"]')).filter(input => input.dataset.nextSlide);
  const dateOfBirthInputs = Array.from(stepContent.querySelectorAll<HTMLInputElement>('input[name="date_of_birth"]')).filter(input => input.dataset.ageBranches);
  let branchingRules: BranchingRules = [];

  radioInputs.forEach(input => {
    branchingRules.push({ inputName: input.name, value: input.value, nextSlideId: input.dataset.nextSlide as string });
  });

  dateOfBirthInputs.forEach(input => {
    const nextSlideAgeCheck = input.dataset.ageBranches!.split('|||').map(ageBranchRule => ageBranchRule.split(', ').map(v => v));
    nextSlideAgeCheck.forEach(branchRule => {
      if ((branchRule[0] || branchRule[1]) && branchRule[2]) {
        branchingRules.push({ inputName: input.name, value: [Number(branchRule[0]), branchRule[1] === '' ? 100 : Number(branchRule[1])], nextSlideId: branchRule[2] });
      }
    });
  });

  const form = stepContent.querySelector<HTMLFormElement>('form');
  const autoSubmit = form?.hasAttribute('data-auto-submit');
  const captcha = stepContent.querySelector<HTMLElement>('.captaincaps');
  let captchaController: CaptchaController | undefined;
  let requireCaptchaOnSubmit = false;
  let failedToFetchSlots = false;

  if (!window.location.hostname.includes('ottonova.de') && !sessionStorage.getItem('alertSeen')) {
    const emailInput = form?.querySelector<HTMLInputElement>('input[type="email"]');
    if (emailInput) {
      emailInput.addEventListener('focusout', () => {
        if (!emailInput.getAttribute('aria-invalid') && !emailInput.value.includes('@ottonova.de') && !sessionStorage.getItem('alertSeen')) {
          document.dispatchEvent(new Event('showMailAlert'));
        }
      });
    }
  }

  if (en) {
    const phoneInput = stepContent.querySelector<HTMLInputElement>('input[name="phone_number"]') as HTMLInputElement;
    if (phoneInput) {
      const countryCode = formStore.values.get().iso2_code || 'us';
      createItiPhoneInput(phoneInput, countryCode as Iso2, stepContent as HTMLElement);
    }
  }

  if (window.location.origin.indexOf('test.on.ag') > -1 || window.location.origin.indexOf('localhost') > -1) {
    window.dispatchEvent(new Event('funnelStepChange'));
  }

  const customerTypeCheckbox = stepContent.querySelector('input[name="customer_type"]') as HTMLInputElement;

  // Find submit button within step content for captcha swap pattern
  const submitButton = stepContent.querySelector<HTMLButtonElement>('button[data-action="submit"]');

  // Initially hide submit button if captcha present - captcha shows first
  if (captcha && submitButton) {
    submitButton.classList.add('is-hidden');
  }

  if (form) {
    const emailInput = form.querySelector<HTMLInputElement>('input[type="email"]');
    const { controller, requireCaptchaOnSubmit: shouldRequireCaptcha } = initCaptchaForForm({
      form,
      mountElement: captcha,
      emailInput,
      locale: en ? 'en' : 'de',
      onShowWidget: () => {
        captcha?.classList.remove('is-hidden');
        submitButton?.classList.add('is-hidden');
      },
      onHideWidget: () => {
        captcha?.classList.add('is-hidden');
        submitButton?.classList.remove('is-hidden');
      }
    });

    captchaController = controller;
    requireCaptchaOnSubmit = shouldRequireCaptcha;

    // Auto-start captcha when widget is shown (email is guaranteed from previous step)
    // Delay to allow widget to fully attach to DOM after transition
    if (captchaController && submitButton && !captchaController.hasUsableToken()) {
      queueMicrotask(() => {
        captchaController?.startIfNeeded();
      });
    }
  }

  const handleSubmit = async (action: string, goto: string = '') => {
    try {
      const formValues = form ? (serializeForm(form) as FormValues) : {};

      const validationResult = validate.validateAll(form);
      if (
        form &&
        ((!failedToFetchSlots && validationResult.length) ||
          (failedToFetchSlots && !Array.from(validationResult).every(field => (field as HTMLElement).classList.contains('dateSelect') || (field as HTMLElement).classList.contains('timeSelect'))))
      ) {
        notificationHandler.setValidateError(validationResult, document.querySelector<HTMLButtonElement>('.funnel-step button[data-action="next"]'));
        return;
      }
      if (customerTypeCheckbox) {
        stores.formStore.values.set({ ...stores.formStore.values.get(), customer_type: customerTypeCheckbox.checked ? 'on' : 'off' });
      }

      stepStatus.set(StepStatus.Submitting);

      let nextSlideId: string = '';
      switch (action) {
        case 'submit':
          await startSpan('consultation.lead.submit', 'function', { flowAction: action, goto: goto || 'consultation-success' }, async () => {
            formStore.submit(formValues, true);

            const parsedFormValues = formStore.parsed.get();

            const previousAppointment = await startSpan('consultation.appointment.lookup', 'function', { hasEmail: !!parsedFormValues.email, hasPhone: !!parsedFormValues.phone_number }, () =>
              getPreviousAppointment(parsedFormValues.email, parsedFormValues.phone_number)
            );

            const hasActiveAppointment =
              previousAppointment &&
              previousAppointment.status &&
              (dayjs(previousAppointment.start).isBefore(dayjs()) || previousAppointment.status === AppointmentStatus.Pending || previousAppointment.status === AppointmentStatus.Confirmed);

            const hasCallback = previousAppointment && previousAppointment.type === 'callback' && !parsedFormValues.appointment;

            // already booked a consultation?
            if (hasActiveAppointment || hasCallback) {
              await bookedAppointmentStore?.update({
                email: parsedFormValues.email,
                phone_number: parsedFormValues.phone_number,
                appointmentId: previousAppointment?.id,
                appointment: previousAppointment?.id ? previousAppointment : undefined,
                consent_confirmed: previousAppointment?.id ? previousAppointment.status === AppointmentStatus.Confirmed : previousAppointment?.consentConfirmed
              });
              nextSlideId = 'consultation-already-booked';
              return;
            }
            // parsing and submission
            const consultationResponse = await submitConsultationLead(parsedFormValues, captchaController, requireCaptchaOnSubmit);

            if (consultationResponse instanceof Error) {
              throw consultationResponse;
            }
            stepStatus.set(StepStatus.Submitted);

            //tracking
            try {
              await pushToDataLayer(await createPhoneLeadEvent(parsedFormValues, consultationResponse));
              if (parsedFormValues.phone_number) {
                metrics.count('consultation.phone_lead', 1, {
                  attributes: {
                    lead_language: parsedFormValues.consultation_language || (en ? 'en' : 'de'),
                    metric_source: 'marketing-funnel-block',
                    user_agent: navigator.userAgent,
                    uri: window.location.href,
                    ...getPageDimensions()
                  }
                });
              }
            } catch (trackingError) {
              captureException(trackingError, { extra: { action } });
            }

            // persisting booked appointment information
            await bookedAppointmentStore?.update({
              email: parsedFormValues.email,
              phone_number: parsedFormValues.phone_number,
              appointmentId: consultationResponse.appointment_details?.id,
              appointment: consultationResponse.appointment_details,
              consent_confirmed: consultationResponse?.consent_confirmed
            });

            await setPreviousAppointment(
              consultationResponse.appointment_details?.id,
              parsedFormValues.email,
              parsedFormValues.phone_number,
              consultationResponse?.consent_confirmed,
              !consultationResponse.appointment_details
            );

            if (dispatchSubmitEvent) {
              document.dispatchEvent(new Event('funnelSubmitted'));
            }

            nextSlideId = goto || 'consultation-success';
          });
          break;

        default:
          formStore.submit(formValues);
          nextSlideId = checkFunnelRules(funnelRules, formStore.values.get());
          if (nextSlideId) {
            break;
          }
          nextSlideId = getForkedNextSlide(branchingRules, formValues) || goto || '';
          break;
      }
      progressStore.nextStep(nextSlideId);
      stepStatus.off();
    } catch (er) {
      let errorMessage: string = '';
      if (typeof er === 'string') {
        errorMessage = er.toUpperCase();
      } else if (er instanceof Error) {
        errorMessage = er.message;
      }

      if (errorMessage === 'taken') {
        captureMessage('Appointment slot taken', { level: 'info', extra: { action } });
        stepStatus.set(StepStatus.Taken);
      } else {
        captureException(er, { extra: { action } });
        stepStatus.set(StepStatus.SubmitError);
      }
    }
  };

  // form field initialization and prefilling
  if (form) {
    for (const element of stepContent.querySelectorAll<HTMLElement>('[data-form-element]')) {
      let controller: InputController | undefined;
      if (isRadioSelectionElement(element)) {
        controller = autoSubmit ? radioInput(element) : radioInput(element, initialFormValues);
      } else if (isCheckboxSelectionElement(element)) {
        controller = checkboxInput(element, initialFormValues);
      } else {
        controller = textInput(element, initialFormValues);
      }
    }

    const date = stepContent.querySelector<HTMLElement>('.date-select');
    const time = stepContent.querySelector<HTMLElement>('.time-select');

    if (date && time) {
      const languageSelect = stepContent.querySelector('.language-select');
      const languageSelectRadios = languageSelect?.querySelectorAll<HTMLInputElement>('input[type="radio"][name="consultation_language"]');
      const appointmentInput = stepContent.querySelector('input[name="appointment"]') as HTMLInputElement;
      const stepButton = stepContent.querySelector('button[data-action="next"], button[data-action="submit"]');
      const stepButtonInitialText = stepButton?.innerHTML || '';
      const dateController = textInput(date, initialFormValues);
      const timeController = textInput(time, initialFormValues);
      const dateInput = dateController?.input as HTMLInputElement;
      const timeInput = timeController?.input as HTMLInputElement;
      let calendarIsLoading = false;
      setAppointmentSlots();

      async function setAppointmentSlots(requestTimeout = false, timeout = 0) {
        languageSelect?.classList.add('fetching-appointments');
        if (stepButton) {
          stepButton.innerHTML = `<span>${en ? 'Loading appointments' : 'Termine werden geladen'}<span class="loading-dot">.</span><span class="loading-dot">.</span><span class="loading-dot">.</span></span>`;
        }
        dateInput.closest('.otto-form-element')?.classList.add('is-disabled');
        timeInput.closest('.otto-form-element')?.classList.add('is-disabled');

        if (requestTimeout) {
          await new Promise(resolve => setTimeout(resolve, timeout));
        }

        try {
          const appointmentSlots = (await calendarStore?.get()) || [];
          languageSelect?.classList.remove('fetching-appointments');
          if (stepButton) {
            stepButton.innerHTML = stepButtonInitialText;
          }
          dateInput.closest('.otto-form-element')?.classList.remove('is-disabled');
          timeInput.closest('.otto-form-element')?.classList.remove('is-disabled');
          fillAppointment({ result: appointmentSlots }, dateInput, timeInput, false);
        } catch {
          const appointmentContainer = stepContent.querySelector<HTMLElement>('.appointment-container');
          if (appointmentContainer) {
            appointmentContainer.style.height = appointmentContainer.offsetHeight + 'px';
          }
          const callbackText = stepContent.querySelector<HTMLElement>('.callback-text');
          callbackText?.classList.remove('is-hidden');
          callbackText?.classList.add('show');
          languageSelect?.classList.remove('fetching-appointments');
          if (stepButton) {
            stepButton.innerHTML = stepButtonInitialText;
          }
          dateInput.closest('.otto-form-element')?.classList.add('hide');
          timeInput.closest('.otto-form-element')?.classList.add('hide');
          setTimeout(() => {
            dateInput.closest('.otto-form-element')?.classList.add('is-hidden');
            timeInput.closest('.otto-form-element')?.classList.add('is-hidden');
          }, 300);
          failedToFetchSlots = true;
        }
      }

      let requestTimeout = 0;

      timeInput.addEventListener('change', () => {
        // Span for tracking which slot a user tried to pick (and catching invalid options).
        startSpan('consultation.appointment.select', 'ui.action', { value: timeInput.value }, () => {
          const option = timeInput.querySelector<HTMLElement>(`option[value="${timeInput.value}"]`);
          if (!option) {
            const error = new Error('invalid appointment time');
            captureException(error, { extra: { value: timeInput.value } });
            throw error;
          }
          appointmentInput.value = JSON.stringify({ start: option.dataset.start, end: option.dataset.end });
        });
      });

      languageSelectRadios?.forEach(radio => {
        radio.addEventListener('change', async () => {
          if (!radio.checked || calendarIsLoading) {
            return;
          }

          const newCalendar = getCalendarFromCategory(formStore.values.get().insurance_category, radio.value as Language);
          if (newCalendar === formStore.values.get().calendar) {
            return;
          }

          formStore.values.set({
            ...formStore.values.get(),
            calendar: newCalendar,
            consultation_language: radio.value as Language
          });

          if (!failedToFetchSlots) {
            calendarIsLoading = true;
            dateController?.resetOptions?.();
            timeController?.resetOptions?.();
            await setAppointmentSlots(true, requestTimeout);
            requestTimeout = Math.min(requestTimeout === 0 ? 100 : requestTimeout * 2, 20000);
            calendarIsLoading = false;
            languageSelectRadios?.forEach(
              radio => (radio.checked = ((radio.value as Language) === 'en' && newCalendar === CalendarType.SalesExpat) || ((radio.value as Language) === 'de' && newCalendar === CalendarType.Sales))
            );
          }
        });
      });
    }

    if (autoSubmit) {
      let lastInputMethod: null | string = null;

      form.addEventListener('mousedown', () => {
        lastInputMethod = 'mouse';
      });

      form.addEventListener('mousemove', () => {
        lastInputMethod = 'mouse';
      });

      // Verfolge Tastatur-Events
      form.addEventListener('keydown', () => {
        lastInputMethod = 'keyboard';
      });

      form.addEventListener('change', () => {
        if (lastInputMethod !== 'keyboard') {
          handleSubmit('next', '');
        }
      });
    }
    form.addEventListener('submit', ev => {
      ev.preventDefault();
      handleSubmit('next', '');
    });
  }

  // button initialization
  for (const button of stepContent.querySelectorAll<HTMLButtonElement>('[data-action]')) {
    let unlistenStepStatus: (() => void) | undefined;
    button.addEventListener('click', async ev => {
      ev.preventDefault();
      if (stepStatus.get() === StepStatus.Submitting) {
        return;
      }

      if (button.dataset.action === 'submit' || button.dataset.action === 'next') {
        let feedbackTimeout: number | undefined;
        window.clearTimeout(feedbackTimeout);
        const defaultLabel = button.innerHTML;

        unlistenStepStatus?.();
        unlistenStepStatus = stepStatus.listen(status => {
          button.disabled = status === StepStatus.Error || status === StepStatus.Submitted;
          switch (status) {
            case StepStatus.Idle:
              button.innerHTML = defaultLabel;

              break;
            case StepStatus.Submitting:
            case StepStatus.Submitted:
              feedbackTimeout = window.setTimeout(() => {
                button.innerHTML = progressText;
              }, 150);
              break;
            case StepStatus.Taken:
              button.innerHTML = en ? 'Retry' : 'Erneut versuchen';
              break;
            case StepStatus.Error:
            case StepStatus.SubmitError:
              button.innerHTML = en ? 'Error' : 'Fehler';
              break;
            default:
              break;
          }
        });
      }

      switch (button.dataset.action) {
        case 'submit':
          await handleSubmit('submit', button.dataset.goto);
          break;
        case 'next':
          await handleSubmit('next', button.dataset.goto);
          break;
        case 'prev':
          progressStore.previousStep();
          break;

        default:
          break;
      }
    });
  }

  for (const link of stepContent.querySelectorAll<HTMLElement>('a.btn')) {
    addTrackingToElement(link, link.classList.contains('btn') ? TagType.CTA : TagType.LINK, {
      element_section: 'marketing-funnel-block',
      element_detail: title
    });
  }

  // modal triggers
  for (const modalTrigger of stepContent.querySelectorAll<HTMLElement>('[data-modal]')) {
    modalTrigger.addEventListener('click', () => {
      MicroModal.show(modalTrigger.dataset.modal as string, {
        onShow: (modal: HTMLElement) => {
          modal.dispatchEvent(new CustomEvent('modalShow'));
        },
        onClose: (modal: HTMLElement) => {
          modal.dispatchEvent(new CustomEvent('modalClose'));
        },
        disableScroll: true,
        closeTrigger: 'data-close'
      });
    });
  }

  //images
  for (const image of stepContent.querySelectorAll<HTMLImageElement>('img[loading="lazy"]')) {
    initImage(image);
  }

  // tooltips with library lazyloading
  for (const tooltip of stepContent.querySelectorAll<HTMLElement>('[data-tooltip]')) {
    if (!window.initTooltip) {
      window.addEventListener(
        'tooltips-loaded',
        () => {
          window.initTooltip(tooltip);
        },
        { once: true }
      );
    } else {
      window.initTooltip(tooltip);
    }
  }

  await fillPlaceHolders(stepContent, initialFormValues, bookedAppointmentStore);
}

async function fillPlaceHolders(element: HTMLElement | DocumentFragment, formValues: FormValues, bookedAppointmentStore?: BookedAppointmentStore) {
  for (const placeholder of element.querySelectorAll<HTMLElement>('[data-funnel-placeholder]')) {
    const fieldName = placeholder.dataset.funnelPlaceholder;
    if (!fieldName) {
      continue;
    }

    if (fieldName === 'consultation_date') {
      const appointmentOrPromise = bookedAppointmentStore?.store.get()?.appointment;

      if (!appointmentOrPromise) {
        return;
      }

      const appointment = appointmentOrPromise instanceof Promise ? await appointmentOrPromise : appointmentOrPromise;

      if (!appointment) {
        placeholder.innerHTML = en ? 'We will get back to you as soon as possible!' : 'Wir werden uns zeitnah bei dir melden!';
        continue;
      }

      const date = new Date(appointment.start);
      const dateString = getFormattedDate(date);
      const timeString = getFormattedTime(date);

      let appointmentMessage = `${en ? '<p>Your scheduled phone call is on' : 'Dein unverbindliches Telefonat ist am'} <br/><strong class="h4 primary">${getFormattedDate(date)} ${
        en ? 'at' : 'um'
      } ${getFormattedTime(date)}</strong>.</p><br>${en ? 'We are looking forward to meet you!' : 'Wir freuen uns, dich kennenzulernen!'}`;

      if (appointment.status === AppointmentStatus.Pending && appointment.doi_enabled) {
        const expiresIn = dayjs(appointment.created_at).add(60, 'minutes').diff(dayjs(), 'minutes');
        appointmentMessage = en
          ? `<p>Your non-binding phone call on the <br/><strong class="h4 primary">${dateString} at ${timeString}</strong><br> is now reserved for <strong class="primary">for ${expiresIn} minutes </strong>.</p><p>We send you a confirmation mail. The appointment will only be booked after you confirm your email address.</p>`
          : `<p>Dein unverbindliches Telefonat am <br/><strong class="h4 primary">${dateString} um ${timeString}</strong><br> ist nun <strong class="primary">${expiresIn} Minuten</strong> für dich reserviert.</p><p>Wir haben dir eine Bestätigungs-E-Mail geschickt. Der Termin ist erst gebucht, sobald du deine E-Mail-Adresse bestätigt hast.</p>`;
      }

      placeholder.innerHTML = appointmentMessage;
      continue;
    }

    const value = formValues[fieldName as keyof FormValues];

    if (value) {
      placeholder.textContent = `${value}`;
    }
  }
}

function getForkedNextSlide(branchingRules: BranchingRules, formValues: FormValues) {
  const entry = branchingRules.find(rule => {
    return Object.entries(formValues).some(([formFieldName, formValue]) => {
      if (formFieldName === rule.inputName) {
        if (!Array.isArray(rule.value)) {
          return rule.value === formValue;
        } else {
          if (formValues.date_of_birth) {
            const ageFromDB = getAgeFromDoB(formValues.date_of_birth.split('.').reverse().join('-'));
            return Number(rule.value[0]) <= ageFromDB && Number(rule.value[1]) >= ageFromDB;
          }
        }
      }
      return false;
    });
  });

  if (!entry) {
    return;
  }
  return entry.nextSlideId.trim();
}

function checkFunnelRules(funnelRules: FunnelRule[], formValues: FormValues) {
  const rejectionFields = (({ date_of_birth, income, employment, jaeg_reached }) => ({ date_of_birth, income, employment, jaeg_reached }))(formValues);
  const filledValues = Object.fromEntries(Object.entries(rejectionFields).filter(([_, value]) => value !== undefined));

  let slideId = '';

  funnelRules.forEach(ruleItem => {
    let ruleMatch = true;
    ruleItem.rules.forEach(ruleConditions => {
      if (!ruleMatch) {
        return;
      }

      const matchingField = filledValues[ruleConditions.field];
      if (!matchingField) {
        ruleMatch = false;
        return;
      }
      ruleMatch = getRuleMatchValue(ruleConditions, matchingField);
    });

    if (ruleMatch) {
      slideId = ruleItem.slideId;
    }
  });

  return slideId;
}

function getRuleMatchValue(ruleField: RuleSet, formFieldValue: string) {
  if (ruleField.field === 'date_of_birth') {
    const ruleAge = parseInt(ruleField.value);
    const fieldAge = getAgeFromDoB(formFieldValue.split('.').reverse().join('-'));
    return ruleField.operator === 'olderThan' ? fieldAge > ruleAge : fieldAge < ruleAge;
  } else {
    return ruleField.operator === 'is' ? ruleField.value === formFieldValue : ruleField.value !== formFieldValue;
  }
}

function isRadioSelectionElement(element: HTMLElement) {
  return element.querySelector('input[type="radio"]') !== null;
}

function isCheckboxSelectionElement(element: HTMLElement) {
  return element.querySelector('input[type="checkbox"]') !== null;
}

export async function submitConsultationLead(formValues: ParsedFormValues, captcha?: CaptchaController, requireCaptcha = false) {
  const payload = formValuesToConsultationPayload(formValues);
  await attachTrackingPayload(payload);
  const response = await postToLeadWithCaptcha('/protected/consultation', payload, {
    captcha,
    requireCaptcha
  });
  if (response instanceof Error) {
    return response as Error;
  } else {
    return response as ConsultationResponse;
  }
}
